'use client';

import React, { useState } from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  Info,
  FileText,
  Mic,
  Brain,
  Code,
  CheckCircle,
  Copy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export function VettingStory() {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        How We Vet Our Candidates, what makes us different from others?
      </h2>

      <div className="space-y-4 text-gray-600 leading-relaxed">
        <p>
          Every candidate's journey begins when they submit their resume. We use{' '}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                Google Gemini and Mastra.ai
                <Info className="w-3 h-3" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b pb-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold">Resume Extraction & Parsing</h4>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    What they do:
                  </p>
                  <p className="text-xs text-gray-600">
                    <strong>Google Gemini:</strong> Uses advanced NLP to extract
                    structured data from resumes, identifying skills,
                    experience, education, and achievements.
                  </p>
                  <p className="text-xs text-gray-600">
                    <strong>Mastra.ai:</strong> Orchestrates the workflow,
                    validates extracted data, and creates the candidate profile.
                  </p>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-xs font-semibold text-gray-700">
                      EXTRACTION PROMPT:
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() =>
                        copyToClipboard(
                          `Extract from this resume: 1) Full name and contact details 2) Years of experience by technology 3) Education (degree, institution, dates) 4) Work experience with companies, titles, dates, and key responsibilities 5) Technical skills categorized by proficiency 6) Notable projects with descriptions and technologies used. Format as structured JSON.`,
                          'Extraction prompt'
                        )
                      }
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs font-mono text-gray-600">
                    "Extract from this resume: 1) Full name and contact details
                    2) Years of experience by technology 3) Education (degree,
                    institution, dates) 4) Work experience with companies,
                    titles, dates, and key responsibilities..."
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>{' '}
          to automatically extract and structure all relevant information from
          their CV, creating a comprehensive candidate profile in seconds.
        </p>

        <p>
          Next, candidates engage in a 15-20 minute conversation with our{' '}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                ElevenLabs AI voice agent
                <Info className="w-3 h-3" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b pb-2">
                  <Mic className="w-4 h-4 text-purple-600" />
                  <h4 className="font-semibold">AI Voice Interview</h4>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    What it does:
                  </p>
                  <p className="text-xs text-gray-600">
                    ElevenLabs' conversational AI conducts natural voice
                    interviews, adapting questions based on responses and
                    evaluating both technical knowledge and communication
                    skills.
                  </p>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    SAMPLE INTERVIEW QUESTIONS:
                  </p>
                  <ul className="space-y-1.5 text-xs font-mono text-gray-600">
                    <li>
                      • "Explain how React hooks work and when you would use
                      useCallback vs useMemo?"
                    </li>
                    <li>
                      • "How does the Node.js event loop handle asynchronous
                      operations?"
                    </li>
                    <li>
                      • "Describe a challenging bug you solved and your
                      debugging process."
                    </li>
                    <li>
                      • "How would you optimize a React app rendering large
                      lists?"
                    </li>
                    <li>
                      • "What's your approach to handling authentication in REST
                      APIs?"
                    </li>
                  </ul>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="mt-2 h-6 text-xs"
                    onClick={() =>
                      copyToClipboard(
                        `1. Explain how React hooks work and when you would use useCallback vs useMemo?
2. How does the Node.js event loop handle asynchronous operations?
3. Describe a challenging bug you solved and your debugging process.
4. How would you optimize a React app rendering large lists?
5. What's your approach to handling authentication in REST APIs?`,
                        'Interview questions'
                      )
                    }
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy questions
                  </Button>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
          . The AI asks technical questions relevant to their experience, diving
          deeper based on their responses. Every word is transcribed for
          comprehensive evaluation.
        </p>

        <p>
          After the interview,{' '}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                Claude AI analyzes the transcript based on an expert-informed prompt
                <Info className="w-3 h-3" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b pb-2">
                  <Brain className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-semibold">Experience Assessment</h4>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    What it does:
                  </p>
                  <p className="text-xs text-gray-600">
                    Claude AI evaluates the interview transcript for technical
                    depth, problem-solving ability, and communication skills. It
                    determines seniority level and identifies strengths and
                    areas for improvement.
                  </p>
                </div>

                <div className="bg-gray-50 rounded p-3 max-h-64 overflow-y-auto">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-xs font-semibold text-gray-700">
                      EVALUATION PROMPT:
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() =>
                        copyToClipboard(
                          `You are a CTO evaluating a Senior Developer candidate. Analyze the interview transcript and assess:

Technical Competence (0-100):
• Depth of knowledge in claimed technologies
• Understanding of system design and architecture
• Problem-solving approach and methodology

Communication Skills (0-100):
• Clarity in explaining technical concepts
• Ability to articulate thought process
• Professional communication style

Seniority Assessment:
• Years of relevant experience
• Leadership and mentorship capabilities
• Strategic thinking and decision-making

Provide specific examples from the transcript to support your scoring. Identify key strengths and areas for development.`,
                          'Evaluation prompt'
                        )
                      }
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                    {`You are a CTO evaluating a Senior Developer candidate. Analyze the interview transcript and assess:

Technical Competence (0-100):
• Depth of knowledge in claimed technologies
• Understanding of system design and architecture
• Problem-solving approach and methodology

Communication Skills (0-100):
• Clarity in explaining technical concepts
• Ability to articulate thought process
• Professional communication style

Seniority Assessment:
• Years of relevant experience
• Leadership and mentorship capabilities
• Strategic thinking and decision-making

Provide specific examples from the transcript to support your scoring.`}
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>{' '}
          to evaluate technical competence, communication skills, and determine
          their seniority level. The AI identifies specific strengths and areas
          where the candidate excels.
        </p>

        <p>
          The real test comes when candidates tackle{' '}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                live coding challenges on Coderbyte
                <Info className="w-3 h-3" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b pb-2">
                  <Code className="w-4 h-4 text-orange-600" />
                  <h4 className="font-semibold">Coding Assessment</h4>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    What it does:
                  </p>
                  <p className="text-xs text-gray-600">
                    Coderbyte provides industry-standard coding challenges that
                    test real-world programming skills. We record the entire
                    session to see how candidates approach problems, debug
                    issues, and optimize solutions.
                  </p>
                </div>

                <div className="bg-gray-50 rounded p-3">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    SAMPLE CHALLENGES:
                  </p>
                  <ul className="space-y-1.5 text-xs text-gray-600">
                    <li className="font-mono">
                      • Array Manipulation: "Find the longest consecutive
                      sequence in an unsorted array"
                    </li>
                    <li className="font-mono">
                      • Tree Traversal: "Serialize and deserialize a binary
                      tree"
                    </li>
                    <li className="font-mono">
                      • API Design: "Build a rate limiter for a REST API"
                    </li>
                    <li className="font-mono">
                      • String Algorithm: "Implement text search with wildcards"
                    </li>
                    <li className="font-mono">
                      • System Design: "Design a URL shortener service"
                    </li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-2">
                    60-90 minutes • Multiple problems • All major languages
                    supported
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
          . For 60–90 minutes, candidates solve real-world problems while we track their problem-solving, debugging, and code optimization in real time, producing objective insights and clear measures of code quality.
        </p>

        <p>
          Finally,{' '}
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">
                Claude AI performs an expert-informed, structured review of their code
                <Info className="w-3 h-3" />
              </button>
            </HoverCardTrigger>
            <HoverCardContent className="w-96" side="top">
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b pb-2">
                  <CheckCircle className="w-4 h-4 text-rose-600" />
                  <h4 className="font-semibold">Code Quality Review</h4>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    What it does:
                  </p>
                  <p className="text-xs text-gray-600">
                    Claude AI performs a comprehensive code review, evaluating
                    style, efficiency, error handling, and adherence to best
                    practices. Each aspect is scored to ensure only high-quality
                    code writers are approved.
                  </p>
                </div>

                <div className="bg-gray-50 rounded p-3 max-h-64 overflow-y-auto">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-xs font-semibold text-gray-700">
                      CODE REVIEW PROMPT:
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={() =>
                        copyToClipboard(
                          `Evaluate this code submission across the following criteria (0-100 each):

1. Code Organization & Structure
   - Logical file/function organization
   - Separation of concerns
   - Modularity and reusability

2. Code Style & Readability
   - Consistent naming conventions
   - Clear variable/function names
   - Appropriate comments and documentation

3. Error Handling & Edge Cases
   - Comprehensive error handling
   - Input validation
   - Edge case consideration

4. Performance & Efficiency
   - Algorithm efficiency
   - Resource optimization
   - Avoiding unnecessary operations

5. Best Practices & Patterns
   - Design pattern usage
   - Security considerations
   - Testing approach

Provide specific examples of good and bad practices found in the code.`,
                          'Code review prompt'
                        )
                      }
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs font-mono text-gray-600 whitespace-pre-wrap">
                    {`Evaluate this code submission across the following criteria (0-100 each):

1. Code Organization & Structure
   - Logical file/function organization
   - Separation of concerns
   - Modularity and reusability

2. Code Style & Readability
   - Consistent naming conventions
   - Clear variable/function names
   - Appropriate comments

3. Error Handling & Edge Cases
   - Comprehensive error handling
   - Input validation
   - Edge case consideration

4. Performance & Efficiency
   - Algorithm efficiency
   - Resource optimization

5. Best Practices & Patterns
   - Design pattern usage
   - Security considerations`}
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>{' '}
          for quality, efficiency, and best practices. We evaluate code
          organization, error handling, performance optimization, and adherence
          to industry standards. Only candidates scoring 70% or higher across
          all assessments make it to our platform.
        </p>

        <p className="font-medium text-gray-900 pt-2 border-t">
          Cruit Europe is a fully AI-driven tech agency that revolutionizes talent acquisition by vetting hundreds of candidates simultaneously through adaptive AI interviews and technical assessments, delivering the top 1% of talent with the speed and precision startups need to scale.
        </p>
      </div>
    </div>
  );
}
